import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { 
  HomeIcon,
  ChartBarIcon,
  BellIcon,
  Cog6ToothIcon,
  ArrowsRightLeftIcon
} from '@heroicons/react/24/outline';
import { 
  HomeIcon as HomeIconSolid,
  ChartBarIcon as ChartBarIconSolid,
  BellIcon as BellIconSolid,
  Cog6ToothIcon as Cog6ToothIconSolid
} from '@heroicons/react/24/solid';
import TransactionModal from './TransactionModal';
import { useNotification } from './NotificationContext';
import useTranslation from '../services/translationService';
import { useSettingsModal } from './SettingsModalContext';
import { isUserRestricted } from '../services/breakglassService';

const TabBar = () => {
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [isRestricted, setIsRestricted] = useState(false);
  const { unreadCount } = useNotification();
  const { t } = useTranslation();
  const { isSettingsOpen, openSettings, closeSettings } = useSettingsModal();
  const location = useLocation();

  // Check if user is in restricted mode
  useEffect(() => {
    const checkRestriction = async () => {
      const restricted = await isUserRestricted();
      setIsRestricted(restricted);
    };
    checkRestriction();
  }, []);

  // Hide TabBar completely for restricted users
  if (isRestricted) {
    return null;
  }

  const tabs = [
    {
      path: '/dashboard',
      label: t('navigation.dashboard'),
      IconOutline: HomeIcon,
      IconSolid: HomeIconSolid
    },
    {
      path: '/reports',
      label: t('navigation.reports'),
      IconOutline: ChartBarIcon,
      IconSolid: ChartBarIconSolid
    },
    {
      path: '/notifications',
      label: t('navigation.notifications'),
      IconOutline: BellIcon,
      IconSolid: BellIconSolid,
      badge: unreadCount > 0 ? unreadCount : null
    },
    {
      action: openSettings,
      label: t('navigation.settings'),
      IconOutline: Cog6ToothIcon,
      IconSolid: Cog6ToothIconSolid,
      isSettings: true
    }
  ];

  // Settings opens full-screen over whatever route is current, so it owns the active state while open
  const isTabActive = (tab) => (isSettingsOpen ? Boolean(tab.isSettings) : tab.path === location.pathname);

  const renderTab = (tab) => (
    <TabButton key={tab.label} {...tab} isActive={isTabActive(tab)} onNavigate={closeSettings} />
  );

  return (
    <>
      <nav className="tab-bar">
        <div className="tab-bar-content">
          {tabs.slice(0, 2).map(renderTab)}
          <div className="action-button-wrapper">
            <button
              type="button"
              onClick={() => setShowTransactionModal(true)}
              className="action-button"
              aria-label={t('transactions.title')}
            >
              <ArrowsRightLeftIcon className="w-6 h-6" />
            </button>
          </div>
          {tabs.slice(2).map(renderTab)}
        </div>
      </nav>

      <TransactionModal
        isOpen={showTransactionModal}
        onClose={() => setShowTransactionModal(false)}
        onSubmit={(data) => {
          console.log('Transaction:', data);
        }}
      />
    </>
  );
};

const TabButton = ({ path, action, label, IconOutline, IconSolid, badge, isActive, onNavigate }) => {
  const Icon = isActive ? IconSolid : IconOutline;
  const className = `tab-button ${isActive ? 'tab-button-active' : 'tab-button-inactive'}`;
  const content = (
    <>
      <span className="relative">
        <Icon className="w-6 h-6" aria-hidden="true" />
        {badge && (
          <span className="absolute -top-1.5 -end-2.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-[10px] font-semibold leading-none text-white ring-2 ring-white dark:ring-gray-900">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </span>
      <span className="max-w-full truncate leading-normal">{label}</span>
    </>
  );

  if (action) {
    return (
      <button type="button" onClick={action} className={className} aria-current={isActive ? 'page' : undefined}>
        {content}
      </button>
    );
  }

  return (
    <Link to={path} onClick={onNavigate} className={className} aria-current={isActive ? 'page' : undefined}>
      {content}
    </Link>
  );
};

export default TabBar; 
